import { ChatMessageType } from "../contracts";
import { isRecord } from "../utils/objectValueConversion";
import type { ChatMessageViewModel } from "./chatMessageViewModel";
import { isCommercialSimulatorSubBrandToolRequest, type CommercialSimulatorOptions, type CommercialSimulatorSubBrandOptions, type CommercialSimulatorSubBrandToolRequest, type CommercialSimulatorToolRequest, type ToolRequest } from "./toolRequestModels";

export type ToolResponse<
    TToolCall extends ToolRequest = ToolRequest,
    TUserSelection = unknown
> = {
    tool_call: TToolCall;
    tool_call_user_selection: TUserSelection;
};

export type CommercialSimulatorToolName = "commercial_simulator_sku";

export type CommercialSimulatorToolInputRetailer = {
    retailer_name?: string;
};

export type CommercialSimulatorToolResponse = ToolResponse<
    CommercialSimulatorToolRequest,
    CommercialSimulatorOptions
>;

export type CommercialSimulatorSubBrandToolResponse = ToolResponse<
    CommercialSimulatorSubBrandToolRequest,
    CommercialSimulatorSubBrandOptions
>;

export const isToolResponse = (type: ChatMessageType, payload: unknown): payload is ToolResponse => {
    if (!isRecord(payload) || type !== ChatMessageType.ToolResponse) {
        return false;
    }
    const toolCall = payload.tool_call;
    if (!isRecord(toolCall)) {
        return false;
    }

    return (
        toolCall.type === "tool_use"
        && typeof toolCall.id === "string"
        && "tool_call_user_selection" in payload
    );
};

export const getToolResponseMessageById = <TToolResponse extends ToolResponse = ToolResponse>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): { message: ChatMessageViewModel; payload: TToolResponse } | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        if (message.type !== ChatMessageType.ToolResponse) {
            continue;
        }

        const payload = message.payload;
        if (isToolResponse(message.type, payload) && payload.tool_call.id === toolRequestId) {
            return { message, payload: payload as TToolResponse };
        }
    }

    return undefined;
};
export const getToolResponseById = <TToolResponse extends ToolResponse = ToolResponse>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): TToolResponse | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        if (message.type !== ChatMessageType.ToolResponse) {
            continue;
        }

        const payload = message.payload;
        if (isToolResponse(message.type, payload) && payload.tool_call.id === toolRequestId) {
            return payload as TToolResponse;
        }
    }

    return undefined;
};

export const isCommercialSimulatorToolResponse = (
    payload: unknown
): payload is CommercialSimulatorToolResponse => {
    if (!isRecord(payload)) {
        return false;
    }

    const toolCall = payload.tool_call;

    if (!isRecord(toolCall)) {
        return false;
    }

    return (
        toolCall.type === "tool_use"
        && typeof toolCall.id === "string"
        && toolCall.name === "commercial_simulator_sku"
        && "tool_call_user_selection" in payload
    );
};

export const isCommercialSimulatorSubBrandToolResponse = (
    payload: unknown
): payload is CommercialSimulatorSubBrandToolResponse => {
    if (!isRecord(payload)) {
        return false;
    }

    const toolCall = payload.tool_call;
    const userSelection = payload.tool_call_user_selection;

    return (
        isCommercialSimulatorSubBrandToolRequest(toolCall)
        && isRecord(userSelection)
        && Array.isArray(userSelection.products)
    );
};
